import type { SpanProcessor } from "@opentelemetry/sdk-trace-web";
import { useEffect } from "react";

export function useTelemetry(
	otlpEndpoint: string | undefined,
	otlpDev: boolean | undefined,
	version: string | undefined,
) {
	useEffect(() => {
		if (!otlpEndpoint && !otlpDev) {
			return;
		}

		let cancelled = false;

		async function initTelemetry() {
			const [
				{ W3CTraceContextPropagator },
				{ OTLPTraceExporter },
				{ registerInstrumentations },
				{ FetchInstrumentation },
				{ XMLHttpRequestInstrumentation },
				{ resourceFromAttributes },
				{ BatchSpanProcessor, ConsoleSpanExporter, TraceIdRatioBasedSampler },
				{ WebTracerProvider },
				{ ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION },
			] = await Promise.all([
				import("@opentelemetry/core"),
				import("@opentelemetry/exporter-trace-otlp-http"),
				import("@opentelemetry/instrumentation"),
				import("@opentelemetry/instrumentation-fetch"),
				import("@opentelemetry/instrumentation-xml-http-request"),
				import("@opentelemetry/resources"),
				import("@opentelemetry/sdk-trace-base"),
				import("@opentelemetry/sdk-trace-web"),
				import("@opentelemetry/semantic-conventions"),
			]);

			if (cancelled) return;

			const exporter = otlpEndpoint
				? new OTLPTraceExporter({ url: `${otlpEndpoint}/v1/traces` })
				: Boolean(otlpDev) && new ConsoleSpanExporter();

			const spanProcessors = [
				exporter && new BatchSpanProcessor(exporter),
			].filter((e) => !!e);

			const provider = new WebTracerProvider({
				resource: resourceFromAttributes({
					[ATTR_SERVICE_NAME]: "platform-web",
					[ATTR_SERVICE_VERSION]: version?.split(":").at(1) || "1.0.0",
				}),
				sampler: new TraceIdRatioBasedSampler(0.2),
				spanProcessors: spanProcessors as unknown as SpanProcessor[],
			});

			// Only the Sentry SDK may write `sentry-trace`. Written here, it carries
			// this tracer's 20% sampling decision, which the server inherits in
			// place of the Sentry SDK's (MKP-2245).
			provider.register({
				propagator: new W3CTraceContextPropagator(),
			});

			registerInstrumentations({
				instrumentations: [
					new FetchInstrumentation(),
					new XMLHttpRequestInstrumentation(),
				],
			});
			if (otlpDev) {
				console.log("OpenTelemetry tracing initialized for frontend");
			}
		}

		void initTelemetry();

		return () => {
			cancelled = true;
		};
	}, [otlpEndpoint, otlpDev, version]);
}
